import React, { useState, useEffect, useCallback, useRef } from "react";
import { SessionModal, SessionCard } from "@/components";
import { Container, Row, Col, Card, Pagination, Dropdown } from "react-bootstrap";
import CommonLayout from "./commonLayout";
import { SessionCardLoader } from "@/components/loder/index";
import { constant } from "@/utils/constant";
import JotFormComponent from "@/components/jotzform";
import { parseJwt } from "../utils/custom";
import { useLocation, useNavigate } from "react-router-dom";
import { PracticeRunAboutModel, PracticeRunsessionModal } from '../components/modal';

const didPageLoadFromReload = () => {
  const navigationEntry = performance.getEntriesByType("navigation")[0];

  if (navigationEntry?.type) {
    return navigationEntry.type === "reload";
  }

  if (performance.navigation) {
    return performance.navigation.type === performance.navigation.TYPE_RELOAD;
  }

  return false;
};

const Session = ({ socket }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAscending, setAscending] = useState(() => {
    if (didPageLoadFromReload()) {
      return true;
    }

    return !location.state ? true : location.state?.is_ascending;
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [show, setShow] = useState(false);
  const [showPracticeRun, setShowPracticeRun] = useState(false);
  const [showPracticeRunSession, setShowPracticeRunSession] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [userData, setUserData] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const sessionListListenerRef = useRef(null);
  const userDataListenerRef = useRef(null);
  const currentFiltersRef = useRef({
    isAscending,
    currentPage,
  });

  useEffect(() => {
    currentFiltersRef.current = {
      isAscending,
      currentPage,
    };
  }, [currentPage, isAscending]);

  const fetchUserData = useCallback(async () => {
    try {
      setUserLoading(true);
      const decodedJwt = parseJwt(localStorage.getItem("token"));

      if (userDataListenerRef.current) {
        socket.off("fetch-single-user-data", userDataListenerRef.current);
      }

      const handleUserData = async (response) => {
        if (response.status === true) {
          // IDOR Protection: Set user credentials for authorization BEFORE setting userData
          // This ensures credentials are on server before any subsequent requests
          if (response.data?.user?.uuid && response.data?.organization?.id) {
            await socket.setUserCredentials(
              response.data.user.uuid,
              response.data.organization.id
            );
          }
          setUserData(response.data);
        }
        setUserLoading(false);
        socket.off("fetch-single-user-data", handleUserData);
        if (userDataListenerRef.current === handleUserData) {
          userDataListenerRef.current = null;
        }
      };

      userDataListenerRef.current = handleUserData;
      socket.on("fetch-single-user-data", handleUserData);
      socket.emit("fetch-single-user-data", {
        user_id: decodedJwt.sub,
      });
    } catch (error) {
      setUserLoading(false);
    }
  }, [socket]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const fetchSessionData = useCallback(
    async ({ overrideIsAscending, overrideCurrentPage } = {}) => {
      try {
        const ascending =
          overrideIsAscending !== undefined
            ? overrideIsAscending
            : currentFiltersRef.current.isAscending;
        const page =
          overrideCurrentPage !== undefined
            ? overrideCurrentPage
            : currentFiltersRef.current.currentPage;

        if (userData?.organization?.id) {
          setIsLoading(true);

          if (sessionListListenerRef.current) {
            socket.off(
              "fetch-session-data-list",
              sessionListListenerRef.current
            );
          }

          const handleSessionList = (response) => {
            if (response.status === true) {
              setData(response.data);
            }
            setIsLoading(false);
            socket.off("fetch-session-data-list", handleSessionList);
            if (sessionListListenerRef.current === handleSessionList) {
              sessionListListenerRef.current = null;
            }
          };

          sessionListListenerRef.current = handleSessionList;
          socket.on("fetch-session-data-list", handleSessionList);
          socket.emit("fetch-session-data-list", {
            id: userData?.organization?.id,
            isAscending: ascending,
            currentPage: page,
          });
        }
      } catch (error) {
        setIsLoading(false);
      }
    },
    [socket, userData?.organization?.id]
  );

  useEffect(() => {
    if (!userData?.organization?.id || !socket) return;

    // Join organization room
    socket.emit("join-organization-room", {
      organization_id: userData.organization.id,
    });

    const handleSessionUpdate = async () => {
      setData(null);
      await fetchSessionData();
      // to get the latest user data with organization data
      await fetchUserData();
    };

    socket.on("res-joined-organization-room", (response) => {
      if (response.success) {
        handleSessionUpdate();
      }
      socket.off("res-joined-organization-room");
    });

    socket.on("organization-session-updated", handleSessionUpdate);

    return () => {
      if (sessionListListenerRef.current) {
        socket.off("fetch-session-data-list", sessionListListenerRef.current);
        sessionListListenerRef.current = null;
      }
      if (userDataListenerRef.current) {
        socket.off("fetch-single-user-data", userDataListenerRef.current);
        userDataListenerRef.current = null;
      }
      socket.off("res-joined-organization-room");
      socket.off("organization-session-updated", handleSessionUpdate);
      if (userData?.organization?.id) {
        socket.emit("leave-organization-room", {
          organization_id: userData.organization.id,
        });
      }
    };
  }, [fetchSessionData, fetchUserData, socket, userData?.organization?.id]);

  const purchaseModal = () => {
    setShowPurchaseModal(!showPurchaseModal);
  };

  const handleAscending = async (isBool) => {
    setData(null);
    setCurrentPage(1);
    setAscending(isBool);
    fetchSessionData({ overrideIsAscending: isBool, overrideCurrentPage: 1 });
    navigate("/session", {
      state: {
        is_ascending: isBool,
      },
    });
  };

  const handlePageChange = async (newPage) => {
    const totalPage = data && data?.totalPages;
    if (newPage <= totalPage && newPage >= 1) {
      setCurrentPage(newPage);
      fetchSessionData({ overrideCurrentPage: newPage });
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  const renderPurchaseMessage = () => {
    const message =
      data?.session?.length >= 1
        ? "Schedule or manage sessions below."
        : "Your organization has no upcoming sessions. Schedule a new session below.";

    return (
      <p className="mb-10 text-raven text-15">{isAscending ? message : ""}</p>
    );
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 10;
    const ellipsis = <Pagination.Ellipsis />;
    const keyPrefix = "page";
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = startPage + maxPagesToShow - 1;
    if (endPage > data?.totalPages) {
      endPage = data?.totalPages;
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    if (startPage > 1) {
      pages.push(
        React.cloneElement(ellipsis, { key: `${keyPrefix}_ellipsis_start` })
      );
    }
    for (let page = startPage; page <= endPage; page++) {
      pages.push(
        <Pagination.Item
          key={`${keyPrefix}_${page}`}
          active={page === currentPage}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </Pagination.Item>
      );
    }
    if (endPage < data?.totalPages) {
      pages.push(
        React.cloneElement(ellipsis, { key: `${keyPrefix}_ellipsis_end` })
      );
      pages.push(
        <Pagination.Item
          key={`${keyPrefix}_${data?.totalPages}`}
          active={data?.totalPages === currentPage}
          onClick={() => handlePageChange(data?.totalPages)}
        >
          {data?.totalPages}
        </Pagination.Item>
      );
    }

    return pages;
  };

  const handleShow = () => {
    setShow((prevShow) => !prevShow);
  };

	const handleShowPracticeRun = () => {
    setShowPracticeRun(prevShow => !prevShow);
  };

  return (
    <>
      {isLoading || userLoading || data?.session === null ? (
        <SessionCardLoader />
      ) : (
        <CommonLayout
          isAscending={isAscending}
          handleAscending={handleAscending}
          handleShow={handleShow}
          handleShowPracticeRun={handleShowPracticeRun}
          userData={userData}
          socket={socket}
        >
          <Container className="mt-8">
            {[3, 4].includes(userData?.user?.role_id) &&
              renderPurchaseMessage()}
            <Row className='mb-8 gy-7 gx-md-10 gx-2xl-20'>
              {isAscending && (
                <Col lg={4} md={6}>
                  <Card bg="transparent" className="session-card h-full">
                    <Card.Body className="hstack mx-auto">
											<Dropdown className="custom-dropdown">
												<Dropdown.Toggle
													id="dropdown-schedule"
													className="custom-toggle"
												>
													{constant?.scheduleSession}
												</Dropdown.Toggle>

												<Dropdown.Menu className="custom-menu">
													<Dropdown.Item onClick={handleShow}>
														Schedule a Session
													</Dropdown.Item>
													<Dropdown.Item onClick={handleShowPracticeRun}>
														Schedule a Practice Run
													</Dropdown.Item>
												</Dropdown.Menu>
											</Dropdown>
                    </Card.Body>
                  </Card>
                </Col>
              )}
              {data?.session?.map((item, index) => (
                <Col lg={4} md={6} key={index}>
                  <SessionCard data={item} socket={socket} />
                </Col>
              ))}
              {data?.totalPages ? (
                <Col xs={12}>
                  <Pagination>
                    <Pagination.Prev
                      onClick={() => handlePageChange(currentPage - 1)}
                    />
                    {renderPageNumbers()}
                    <Pagination.Next
                      onClick={() => handlePageChange(currentPage + 1)}
                    />
                  </Pagination>
                </Col>
              ) : null}
            </Row>
          </Container>
        </CommonLayout>
      )}
      {show && (
        <SessionModal
          show={show}
          hide={handleShow}
          handleAscending={handleAscending}
          isAscending={isAscending}
          userData={userData}
          socket={socket}
        />
      )}
      {showPurchaseModal && (
        <JotFormComponent show={showPurchaseModal} hide={purchaseModal} />
      )}
			{showPracticeRun && (
				<PracticeRunAboutModel
					show={showPracticeRun}
					hide={handleShowPracticeRun}
					openPracticeRunSessionModel={() => {
						setShowPracticeRunSession(true);
						handleShowPracticeRun();
					}}
				/>
			)}
			{showPracticeRunSession && (
				<PracticeRunsessionModal
					show={showPracticeRunSession}
					hide={()=>{setShowPracticeRunSession(false)}}
					handleAscending={handleAscending}
					isAscending={isAscending}
					userData={userData}
          socket={socket}
				/>
			)}
		</>
	);
};

export default Session;
